import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * Stock levels — FR-INV-02
 *
 * Operational overview:
 *   - 4-tile KPI strip (total products, inventory value, low-stock, out-of-stock)
 *   - Search + status filter chips (All / OK / Low / Out)
 *   - Table with stock indicator bars, status chips, quick links to adjust
 */
export function StockPage() {
  const [q, setQ] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'ok' | 'low' | 'out'>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['stock-products', q],
    queryFn: () => inventoryApi.listProducts({ q: q || undefined, page: 1, pageSize: 200 }),
  });

  const { kpis, filteredRows, maxStock } = useMemo(() => {
    const rows = data?.data ?? [];
    let totalValue = 0;
    let lowCount = 0;
    let outCount = 0;
    let maxQ = 0;
    for (const p of rows) {
      const qty = parseFloat(p.stock_qty) || 0;
      const cost = parseFloat(p.cost_price) || 0;
      totalValue += qty * cost;
      if (qty <= 0) outCount++;
      else if (qty <= 10) lowCount++;
      if (qty > maxQ) maxQ = qty;
    }

    const filtered = rows.filter((p: any) => {
      const qty = parseFloat(p.stock_qty) || 0;
      if (stockFilter === 'out') return qty <= 0;
      if (stockFilter === 'low') return qty > 0 && qty <= 10;
      if (stockFilter === 'ok') return qty > 10;
      return true;
    });

    return {
      kpis: {
        totalProducts: rows.length,
        totalValue,
        lowCount,
        outCount,
      },
      filteredRows: filtered,
      maxStock: Math.max(maxQ, 1),
    };
  }, [data, stockFilter]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Stock levels"
        subtitle="Real-time on-hand quantities, aggregated across all warehouses."
        actions={
          <>
            <Link to="/inventory/adjustments" className="btn-secondary">
              <EditIcon /> Adjustments
            </Link>
            <Link to="/inventory/transfers" className="btn-secondary">
              <MoveIcon /> Transfers
            </Link>
            <Link to="/inventory/counts" className="btn-primary">
              <ClipboardIcon /> Cycle count
            </Link>
          </>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total products" value={kpis.totalProducts} />
        <KpiTile label="Inventory value" value={fmtMoney(kpis.totalValue)} accent="sage" />
        <KpiTile label="Low stock" value={kpis.lowCount}
          accent={kpis.lowCount > 0 ? 'amber' : 'default'} />
        <KpiTile label="Out of stock" value={kpis.outCount}
          accent={kpis.outCount > 0 ? 'danger' : 'default'} />
      </div>

      {/* Toolbar */}
      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex-1 min-w-48 flex items-center gap-2 px-3">
          <SearchIcon className="text-ink-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by name, SKU, or barcode…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-ink-400 py-2 min-w-0"
          />
        </div>
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1">
          {([
            ['all', 'All'],
            ['ok', 'In stock'],
            ['low', 'Low stock'],
            ['out', 'Out of stock'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setStockFilter(key)}
              className={clsx(
                'px-3 py-1 text-xs font-semibold rounded-pill transition-all',
                stockFilter === key
                  ? 'bg-canvas-elevated text-ink-900 shadow-xs'
                  : 'text-ink-600 hover:text-ink-900'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card"><LoadingState rows={6} /></div>
      ) : error ? (
        <ErrorState error={error} />
      ) : !filteredRows.length ? (
        <div className="card">
          <EmptyState
            message={
              q
                ? 'No products match your search'
                : stockFilter === 'out'
                  ? 'No out-of-stock items'
                  : stockFilter === 'low'
                    ? 'No low-stock items'
                    : 'No products to show'
            }
            hint={
              stockFilter === 'out'
                ? 'Good news — every SKU has inventory on hand.'
                : stockFilter === 'low'
                  ? 'All stock levels look healthy.'
                  : undefined
            }
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="w-48">Stock</th>
                  <th>Status</th>
                  <th className="text-right">Value</th>
                  <th className="w-20"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((p: any) => {
                  const qty = parseFloat(p.stock_qty) || 0;
                  const cost = parseFloat(p.cost_price) || 0;
                  const value = qty * cost;
                  const tone = qty <= 0 ? 'danger' : qty <= 10 ? 'warning' : 'success';
                  const label = qty <= 0 ? 'Out of stock' : qty <= 10 ? 'Low stock' : 'In stock';
                  return (
                    <tr key={p.id}>
                      <td>
                        <Link to={`/inventory/products/${p.id}`} className="flex items-center gap-3 group">
                          <div className="w-9 h-9 rounded-md bg-canvas-subtle flex items-center justify-center font-bold text-sm text-ink-600 flex-shrink-0">
                            {p.name.trim()[0]?.toUpperCase() ?? '?'}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-ink-900 group-hover:text-sage-700 transition-colors truncate">
                              {p.name}
                            </div>
                            <div className="font-mono text-xs text-ink-500">{p.sku}</div>
                          </div>
                        </Link>
                      </td>
                      <td>
                        <StockBar qty={qty} max={maxStock} tone={tone} />
                      </td>
                      <td><StatusBadge status={label} tone={tone} /></td>
                      <td className="text-right font-mono text-sm tabular-nums text-ink-700">
                        {fmtMoney(value)}
                      </td>
                      <td className="text-right">
                        <Link
                          to={`/inventory/adjustments`}
                          className="text-xs font-medium text-ink-600 hover:text-ink-900 transition-colors"
                        >
                          Adjust →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// StockBar — quantity visualizer with threshold-aware fill
// ============================================================
function StockBar({ qty, max, tone }: { qty: number; max: number; tone: 'success' | 'warning' | 'danger' }) {
  const pct = Math.min(100, (qty / max) * 100);
  const fill = {
    success: 'bg-sage-500',
    warning: 'bg-warning',
    danger: 'bg-danger',
  }[tone];
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-canvas-subtle rounded-pill overflow-hidden">
        {qty > 0 && (
          <div
            className={`h-full ${fill} rounded-pill transition-all duration-500 ease-out-expo`}
            style={{ width: `${Math.max(pct, 3)}%` }}
          />
        )}
      </div>
      <span className="font-mono font-semibold text-sm text-ink-900 tabular-nums min-w-10 text-right">
        {qty.toFixed(0)}
      </span>
    </div>
  );
}

// ============================================================
// KpiTile
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

// ============================================================
// Helpers + icons
// ============================================================

const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

function SearchIcon({ className = '' }: { className?: string }) { return <svg {...iconProps} className={className}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon() { return <svg {...iconProps}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function MoveIcon() { return <svg {...iconProps}><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>; }
function ClipboardIcon() { return <svg {...iconProps}><path d="M9 2h6a2 2 0 012 2v2h2a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h2V4a2 2 0 012-2z"/><rect x="9" y="2" width="6" height="4" rx="1"/></svg>; }
